import Ionicons from '@expo/vector-icons/Ionicons';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Keyboard, StyleSheet, TouchableOpacity, View } from 'react-native';
import Animated, { useAnimatedRef } from 'react-native-reanimated';

import { Chat, ChatRoom, DeleteMessageData, EditMessageData, NewMessageData } from '@/api/models/chatRoomModels';
import { profilePlaceholder } from '@/assets/images/icons';
import TextInputField from '@/components/inputs/TextInputField';
import { ChatActions } from '@/constants/enums/ChatActions';
import { firebaseImage, safeJsonParseArray } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';

import ChatMessage from './ChatMessage';

interface ChatListWithInputProps {
  chatRoom: ChatRoom;
  chats: Chat[];
  msgSend: (data: NewMessageData) => void;
  msgEdited: (data: EditMessageData) => void;
  msgDeleted: (data: DeleteMessageData) => void;
}

const ChatListWithInput = ({ chatRoom, chats, msgSend, msgEdited, msgDeleted }: ChatListWithInputProps) => {
  const flatListRef = useAnimatedRef<Animated.FlatList<string>>();

  const { setValue, control, handleSubmit } = useForm<NewMessageData>({
    mode: 'onBlur'
  });

  const onMsgSend: SubmitHandler<NewMessageData> = async (data) => {
    if (data.newMessage.length > 0) {
      Keyboard.dismiss();
      setValue('newMessage', '');
      msgSend(data);
    }
  };

  const onMsgEdited: SubmitHandler<EditMessageData> = async (data) => {
    if (data.message.length > 0) {
      Keyboard.dismiss();
      msgEdited(data);
    }
  };

  const onMsgDeleted = async (data: DeleteMessageData) => {
    msgDeleted(data);
  };

  const roomThumbnail = safeJsonParseArray(chatRoom.thumbnail ?? '[]');

  return (
    <View style={styles.container}>
      <Animated.FlatList
        inverted
        ref={flatListRef}
        data={chats}
        keyExtractor={(item: Chat) => item.id}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.flatListContent}
        stickyHeaderIndices={[0]}
        ListHeaderComponent={
          <View style={styles.input}>
            <TextInputField
              name="newMessage"
              control={control}
              placeholder={i18n.t('messages.send_message_placeholder')}
              hideError
              onKeyPress={({ nativeEvent }) => {
                const event = nativeEvent as any;
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault?.();
                  handleSubmit(onMsgSend)();
                }
              }}
              multiline
              minHeight={38}
              maxHeight={100}
              variant="rounded_with_shadow"
              traillingIcon={
                <TouchableOpacity onPress={handleSubmit(onMsgSend)}>
                  <Ionicons size={20} name="send" style={styles.grayColor} />
                </TouchableOpacity>
              }
            />
          </View>
        }
        renderItem={({ item, index }: { item: Chat; index: number }) => {
          const senderThumb =
            item.senderThumb && item.senderThumb !== null && !item.senderThumb.includes('null')
              ? safeJsonParseArray(item.senderThumb ?? '[]')
              : undefined;

          const thumbnail =
            item.senderId === 1
              ? item.senderThumb === null || !senderThumb || senderThumb.length === 0
                ? profilePlaceholder
                : senderThumb && senderThumb.length > 0
                  ? firebaseImage(senderThumb[0])
                  : undefined
              : senderThumb && senderThumb.length > 0
                ? firebaseImage(senderThumb[0])
                : roomThumbnail && roomThumbnail.length > 0
                  ? firebaseImage(roomThumbnail[0])
                  : undefined;

          return (
            <ChatMessage
              roomId={chatRoom.id ?? ''}
              chat={item}
              thumbnail={thumbnail}
              sender={item.sender ?? chatRoom.title ?? '-'}
              roomSubtitle={chatRoom.subtitle}
              actions={[{ title: ChatActions.EDIT }, { title: ChatActions.DELETE, destructive: true }]}
              onEdit={(data) => {
                flatListRef.current?.scrollToIndex({
                  index: index,
                  viewPosition: 0.5,
                  animated: true
                });
                onMsgEdited(data);
              }}
              onDelete={onMsgDeleted}
            />
          );
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  flexOne: {
    flex: 1
  },

  container: {
    flexDirection: 'column',
    height: '100%'
  },

  flatListContent: {
    paddingHorizontal: 18
  },

  divider: {
    height: 20
  },

  input: {
    paddingHorizontal: 18,
    paddingTop: 10,
    paddingBottom: 20,
    maxHeight: 100,
    zIndex: 10
  },

  grayColor: {
    color: '#808080'
  }
});

export default ChatListWithInput;
